/**
 * BEEZFY PRODUCT PREMIUM V3.0
 * Sistema prodotto ultra-performante con integrazioni complete
 * Mobile-first, conversion-focused, fashion e-commerce ready
 */

class BeezfyProductPremium {
  constructor() {
    this.initialized = false;
    this.product = null;
    this.variants = null;
    this.currentVariant = null;
    this.selectedOptions = {};
    this.mediaItems = [];
    this.currentMediaIndex = 0;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.lastClickedSwatch = null;
    this.swatchEnhancementsReady = false;
    this.boundSyncSwatches = null;
    this.swatchDataStore = null;
    this.heroOverrideMeta = null;
    this.heroOverrideTarget = null;
    this.heroObserver = null;
    this.currentSwatchOverride = null;
    this.latestSwatchSelection = null;
    this.swatchSelections = new Map();
    this.optionIndexMap = new Map();
    this.optionCanonicalNames = new Map();
    this.userPickedOptions = new Set(); // opzioni scelte CONSAPEVOLMENTE dall'utente (la PDP auto-seleziona la prima variante)

    this.elements = {};
    this.settings = {
      enableZoom: true,
      enableSwipe: true,
      autoplayVideos: false,
      stickyAddToCart: true
    };

    this.init();
  }

  async init() {
    try {
      // Wait for DOM
      if (document.readyState === 'loading') {
        await new Promise(resolve => document.addEventListener('DOMContentLoaded', resolve));
      }

      // Load product data
      this.loadProductData();

      // Cache elements
      this.cacheElements();

      // Hide blocking popups immediately
      this.hideBlockingPopups();

      // Initialize all features
      this.initializeMedia();
      this.initializeVariants();
      this.initializeSwatchEnhancements();
      this.initializeQuantity();
      this.initializeAddToCart();
      this.setupConsciousVariantTracking();
      this.initializeTabs();
      this.initializeMobile();
      this.initializeAccessibility();

      // Setup observers
      this.setupIntersectionObserver();

      // Hide blocking popups again after everything is initialized
      setTimeout(() => this.hideBlockingPopups(), 1000);

      this.initialized = true;

      // Emit ready event
      this.emit('product:ready', { product: this.product });

    } catch (error) {
      console.error('BeezfyProductPremium initialization error:', error);
    }
  }

  loadProductData() {
    const dataElement = document.querySelector('[data-beezfy-product-json]');
    if (!dataElement) {
      console.error('Product data not found');
      return;
    }

    try {
      const data = JSON.parse(dataElement.textContent);
      this.product = data.product;
      this.variants = data.variants;
      this.media = data.media;

      // Selezione esplicita SOLO se l'utente arriva con ?variant=… (deep link). Altrimenti la PDP
      // si apre SENZA variante pre-selezionata: il cliente sceglie consapevolmente (niente swatch
      // "già selezionato"). L'ATC su prodotto variabile aprirà il wizard (modalità 'incomplete').
      this.hasExplicitVariant = data.hasExplicitVariant === true;
      this.currentVariant = this.hasExplicitVariant
        ? (this.variants.find(v => v.id === data.selectedVariantId) || this.variants[0])
        : null;

      this.initializeOptionMappings();
      if (this.hasExplicitVariant) {
        this.refreshSelectedOptionsFromVariant();
        // deep link = scelta esplicita su tutte le opzioni
        (Array.isArray(this.product?.options) ? this.product.options : [])
          .forEach(o => this.userPickedOptions.add(this.normalizeOptionKey(o)));
      } else {
        // nessuna selezione: svuota le etichette "COLORE: …" (currentVariant null → valore vuoto)
        this.selectedOptions = {};
        this.updateVariantLabelDisplays();
      }
    } catch (error) {
      console.error('Error parsing product data:', error);
    }
  }

  normalizeOptionKey(name) {
    if (!name && name !== 0) return '';
    return name
      .toString()
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ');
  }

  initializeOptionMappings() {
    this.optionIndexMap = new Map();
    this.optionCanonicalNames = new Map();

    const optionNames = Array.isArray(this.product?.options) ? this.product.options : [];
    optionNames.forEach((name, index) => {
      if (!name) return;
      const normalized = this.normalizeOptionKey(name);
      if (!this.optionIndexMap.has(normalized)) {
        this.optionIndexMap.set(normalized, index);
        this.optionCanonicalNames.set(normalized, name);
      }
    });
  }

  getCanonicalOptionName(optionName) {
    const normalized = this.normalizeOptionKey(optionName);
    return this.optionCanonicalNames.get(normalized) || optionName;
  }

  getInputOptionValue(input) {
    if (!input) return '';

    if (input.dataset.optionValue) {
      return input.dataset.optionValue;
    }

    const label = input.closest('label');
    if (label) {
      if (label.dataset.swatchValue) {
        return label.dataset.swatchValue;
      }

      const swatch = label.querySelector('[data-swatch]');
      if (swatch) {
        if (swatch.dataset.optionValue) {
          return swatch.dataset.optionValue;
        }
        if (swatch.dataset.swatchLabel) {
          return swatch.dataset.swatchLabel;
        }
      }

      const button = label.querySelector('[data-option-value]');
      if (button) {
        return button.dataset.optionValue || button.textContent?.trim() || '';
      }
    }

    const explicit = input.getAttribute('value');
    if (explicit) {
      return explicit;
    }

    return input.value;
  }

  refreshSelectedOptionsFromVariant(overrides = {}) {
    const updated = {};

    const optionNames = Array.isArray(this.product?.options) ? this.product.options : [];
    if (this.currentVariant && optionNames.length) {
      optionNames.forEach((name, index) => {
        if (!name) return;
        const value = this.currentVariant.options?.[index];
        if (value === undefined) return;
        const normalized = this.normalizeOptionKey(name);
        updated[name] = value;
        updated[normalized] = value;
      });
    }

    Object.entries(overrides || {}).forEach(([key, value]) => {
      if (value === undefined) return;
      const normalized = this.normalizeOptionKey(key);
      const canonical = this.optionCanonicalNames.get(normalized) || key;
      updated[canonical] = value;
      updated[normalized] = value;
    });

    this.selectedOptions = updated;
    this.updateVariantLabelDisplays();
  }

  getOptionValue(optionName) {
    if (!optionName) return '';
    const normalized = this.normalizeOptionKey(optionName);
    const index = this.optionIndexMap.has(normalized) ? this.optionIndexMap.get(normalized) : null;
    if (index !== null && this.currentVariant?.options) {
      const value = this.currentVariant.options[index];
      if (value !== undefined) {
        return value;
      }
    }
    return this.selectedOptions?.[optionName] ?? this.selectedOptions?.[normalized] ?? '';
  }

  updateVariantLabelDisplays() {
    const labels = document.querySelectorAll('.beezfy-variant-selected, .beezfy-variant__selected[data-selected-value]');
    if (!labels.length) return;

    labels.forEach((label) => {
      let optionName = label.dataset.optionName
        || label.getAttribute('data-option-name')
        || label.closest('[data-option-name]')?.dataset.optionName
        || '';

      if (!optionName) {
        const group = label.closest('.beezfy-variant__group');
        const optionContainer = group?.querySelector('[data-option-name]');
        if (optionContainer?.dataset?.optionName) {
          optionName = optionContainer.dataset.optionName;
        }
      }

      if (!optionName) return;
      const value = this.getOptionValue(optionName) || '';
      if (label.textContent !== value) {
        label.textContent = value;
      }
      if (!label.dataset.optionName && optionName) {
        label.dataset.optionName = optionName;
      }
    });
  }

  handleExternalMediaUpdate() {
    // Refresh cached references in case the gallery was replaced
    this.cacheElements();

    if (!this.latestSwatchSelection) {
      const activeSwatch = this.findActiveColorSwatch();
      if (activeSwatch) {
        this.buildSwatchSelection(activeSwatch);
        this.currentSwatchOverride = activeSwatch;
        this.lastClickedSwatch = activeSwatch;
      }
    }

    this.updateMediaForVariant();
    this.syncSwatchState();
    this.updateVariantLabelDisplays();
  }

  cacheElements() {
    // Main containers
    this.elements.container = document.querySelector('.beezfy-product-premium');

    // Media elements
    this.elements.mediaMain = document.querySelector('[data-media-main]');
    this.elements.mediaItems = document.querySelectorAll('.beezfy-media__item');
    this.elements.thumbnails = document.querySelectorAll('[data-thumbnails] button');
    this.elements.dots = document.querySelectorAll('.beezfy-dot');

    // Form elements
    this.elements.form = document.querySelector('[data-product-form]');
    this.elements.variantInputs = document.querySelectorAll('.beezfy-variant__input');
    this.elements.swatchContainers = document.querySelectorAll('[data-swatches-container]');
    // l'input nascosto del form: NON '[data-variant-id]' da solo, che prende il primo pulsante di opzione (radio) e ne sovrascrive il valore
    this.elements.variantIdInput = (this.elements.form && this.elements.form.querySelector('input[name="id"]')) || document.querySelector('input.product-variant-id');
    this.elements.addToCartBtn = document.querySelector('[data-add-to-cart]');
    this.elements.buyNowBtn = document.querySelector('[data-buy-now]');

    // Quantity
    this.elements.qtyInput = document.querySelector('[data-qty-input]');
    this.elements.qtyBtns = document.querySelectorAll('[data-qty-btn]');

    // Display elements
    this.elements.priceDisplay = document.querySelector('[data-price-display]');
    this.elements.stockDisplay = document.querySelector('[data-stock-display]');

    // Tabs
    this.elements.tabBtns = document.querySelectorAll('[data-tab]');
    this.elements.tabPanels = document.querySelectorAll('[data-panel]');
  }

  // ========== MEDIA GALLERY ==========
  initializeMedia() {
    if (!this.elements.mediaItems.length) return;

    // Thumbnail clicks
    this.elements.thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.handleThumbnailClick(index));
    });

    // Mobile dots
    this.elements.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.switchMedia(index));
    });

    // Touch swipe for mobile
    if (this.settings.enableSwipe && 'ontouchstart' in window) {
      this.initializeSwipe();
    }

    // Zoom on desktop
    if (this.settings.enableZoom && !this.isMobile()) {
      this.initializeZoom();
    }

    // Video controls
    this.initializeVideos();
  }

  handleThumbnailClick(index) {
    this.clearHeroOverride({ force: true });
    this.switchMedia(index);
  }

  switchMedia(index) {
    if (index < 0 || index >= this.elements.mediaItems.length) return;

    this.currentMediaIndex = index;

    // Update active states
    this.elements.mediaItems.forEach((item, i) => {
      item.classList.toggle('active', i === index);
    });

    this.elements.thumbnails.forEach((thumb, i) => {
      thumb.classList.toggle('active', i === index);
    });

    this.elements.dots.forEach((dot, i) => {
      dot.classList.toggle('active', i === index);
    });

    // Pause videos when switching
    this.pauseAllVideos();

    // Emit event
    this.emit('media:changed', { index, media: this.media[index] });
  }

  initializeSwipe() {
    const container = this.elements.mediaMain;

    container.addEventListener('touchstart', (e) => {
      this.touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    container.addEventListener('touchend', (e) => {
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    }, { passive: true });
  }

  handleSwipe() {
    const swipeThreshold = 50;
    const diff = this.touchStartX - this.touchEndX;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        // Swipe left - next
        this.switchMedia(Math.min(this.currentMediaIndex + 1, this.elements.mediaItems.length - 1));
      } else {
        // Swipe right - previous
        this.switchMedia(Math.max(this.currentMediaIndex - 1, 0));
      }
    }
  }

  initializeZoom() {
    const zoomBtn = document.querySelector('.beezfy-media__zoom-btn');
    const images = document.querySelectorAll('[data-zoom-container] img');

    if (!zoomBtn || !images.length) return;

    zoomBtn.addEventListener('click', () => {
      const currentImage = images[this.currentMediaIndex];
      if (currentImage) {
        this.openZoomModal(currentImage);
      }
    });
  }

  openZoomModal(image) {
    // Use lightbox library or custom implementation
    if (window.BeezfyLightbox) {
      window.BeezfyLightbox.open(image.dataset.zoomImage || image.src);
    }
  }

  initializeVideos() {
    const videos = document.querySelectorAll('.beezfy-media__video');
    videos.forEach(video => {
      video.addEventListener('play', () => {
        this.pauseAllVideos(video);
      });
    });
  }

  pauseAllVideos(except = null) {
    const videos = document.querySelectorAll('.beezfy-media__video');
    videos.forEach(video => {
      if (video !== except && !video.paused) {
        video.pause();
      }
    });
  }

  // ========== VARIANTS ==========
  initializeSwatchEnhancements() {
    if (!this.elements.swatchContainers || !this.elements.swatchContainers.length) {
      return;
    }

    this.attachSwatchHeroListeners();

    // Avoid re-binding multiple times while still syncing state
    if (this.swatchEnhancementsReady) {
      this.syncSwatchState();
      return;
    }

    this.swatchEnhancementsReady = true;
    this.syncSwatchState();

    const connectDataStore = (attempt = 0) => {
      const dataStore = window.BeezfySwatchDataStore;

      if (!dataStore) {
        if (attempt < 25) {
          setTimeout(() => connectDataStore(attempt + 1), 120);
        } else {
          console.warn('BeezfyProductPremium: Swatch data store unavailable');
        }
        return;
      }

      this.swatchDataStore = dataStore;

      this.elements.swatchContainers.forEach((container) => {
        try {
          if (this.product?.id && !container.dataset.productId) {
            container.dataset.productId = this.product.id;
          }
          if (this.product?.handle && !container.dataset.productHandle) {
            container.dataset.productHandle = this.product.handle;
          }
          container.dataset.swatchContext = container.dataset.swatchContext || 'pdp';
          dataStore.enrichContainer(container);
        } catch (error) {
          console.warn('BeezfyProductPremium: Failed to enrich swatch container', error);
        }
      });

      if (!this.boundSyncSwatches) {
        this.boundSyncSwatches = this.syncSwatchState.bind(this);
        this.on('variant:changed', this.boundSyncSwatches);
      }

      // Re-sync once enhancements apply asynchronously
      setTimeout(() => this.syncSwatchState(), 60);
      setTimeout(() => this.syncSwatchState(), 300);
    };

    connectDataStore();
  }

  attachSwatchHeroListeners() {
    this.elements.swatchContainers.forEach((container) => {
      if (!container || container.dataset.heroListenerBound === 'true') {
        return;
      }

      container.dataset.heroListenerBound = 'true';
      container.addEventListener('click', (event) => {
        const swatch = event.target.closest('[data-swatch]');
        if (!swatch) return;
        this.lastClickedSwatch = swatch;
        this.handleSwatchHeroClick(swatch, container);
      });
    });
  }

  handleSwatchHeroClick(swatch, container) {
    if (!swatch) return;

    const imageUrl = this.resolveSwatchImage(swatch);
    if (!imageUrl) {
      return;
    }

    const mediaMatch = this.findMediaMatch(imageUrl);
    if (mediaMatch) {
      this.showMediaMatch(mediaMatch);
      return;
    }

    const isBundleSwatch = container?.dataset?.isBundle === 'true' ||
                           container?.dataset?.isBundleOption === 'true';

    this.overrideHeroImage(imageUrl, {
      source: isBundleSwatch ? 'bundle-swatch' : 'swatch',
      optionName: container?.dataset?.optionName || '',
      optionValue: swatch.dataset.optionValue || swatch.dataset.swatchLabel || ''
    });
  }

  resolveSwatchImage(swatch) {
    if (!swatch) return '';
    const dataset = swatch.dataset || {};
    const directImage = dataset.swatchImage || dataset.swatchThumbnail || '';
    if (directImage) return directImage;

    const inlineImg = swatch.querySelector('img');
    if (inlineImg) {
      return inlineImg.currentSrc || inlineImg.src || '';
    }
    return '';
  }

  findMediaMatch(imageUrl) {
    if (!imageUrl) return null;

    const targetFilename = this.stripShopifySizeSuffix(this.extractImageFilename(imageUrl));
    if (!targetFilename) return null;

    const classicItems = Array.from(this.elements.mediaItems || []);
    for (let index = 0; index < classicItems.length; index += 1) {
      const item = classicItems[index];
      if (!item || item.dataset.mediaType !== 'image') continue;
      const img = item.querySelector('img');
      if (!img) continue;

      const candidate = this.stripShopifySizeSuffix(
        this.extractImageFilename(img.currentSrc || img.src || '')
      );

      if (candidate && candidate === targetFilename) {
        return { type: 'classic', index };
      }
    }

    const premiumItems = Array.from(this.elements.container?.querySelectorAll('.beezfy-gallery__media') || []);
    for (let index = 0; index < premiumItems.length; index += 1) {
      const item = premiumItems[index];
      if (!item) continue;
      const img = item.querySelector('img');
      if (!img) continue;

      const candidate = this.stripShopifySizeSuffix(
        this.extractImageFilename(img.currentSrc || img.src || '')
      );

      if (candidate && candidate === targetFilename) {
        return { type: 'premium', index };
      }
    }

    return null;
  }

  extractImageFilename(url) {
    if (!url) return '';
    const cleanUrl = this.normalizeImageUrl(url);
    const segments = cleanUrl.split('/');
    return segments.pop() || '';
  }

  stripShopifySizeSuffix(filename) {
    if (!filename) return '';
    return filename.replace(/_(\d+x\d+|\d+x|x\d+)(?=\.[^.]+$)/i, '');
  }

  normalizeImageUrl(url) {
    if (!url) return '';
    const base = url.split('?')[0];
    return base.replace(/_(\d+x\d+|\d+x|x\d+)(?=\.[^.]+$)/i, '');
  }

  showMediaMatch(match) {
    if (!match) return;

    this.clearHeroOverride({ force: true });

    if (match.type === 'classic') {
      this.switchMedia(match.index);
      return;
    }

    if (match.type === 'premium') {
      const galleryElement = this.elements.container?.querySelector('[data-beezfy-premium-gallery]');
      const galleryInstance = galleryElement ? galleryElement._galleryInstance : null;
      if (galleryInstance && typeof galleryInstance.goToMedia === 'function') {
        galleryInstance.goToMedia(match.index);
      } else {
        const premiumItems = Array.from(this.elements.container?.querySelectorAll('.beezfy-gallery__media') || []);
        premiumItems.forEach((item, index) => {
          item.classList.toggle('active', index === match.index);
        });
      }
    }
  }

  ensureHeroBaseImage() {
    if (this.heroOverrideTarget && document.body.contains(this.heroOverrideTarget.img)) {
      return this.heroOverrideTarget;
    }

    let activeItem = this.elements.mediaMain?.querySelector('.beezfy-media__item.active');
    let galleryType = 'classic';

    if (!activeItem || activeItem.dataset.mediaType !== 'image') {
      const classicItems = Array.from(this.elements.mediaItems || []);
      activeItem = classicItems.find((item) => item.dataset.mediaType === 'image') || null;
      if (activeItem) {
        const index = classicItems.indexOf(activeItem);
        if (index >= 0) {
          this.switchMedia(index);
        }
      }
    }

    if (!activeItem) {
      const premiumActive = this.elements.container?.querySelector('.beezfy-gallery__media.active');
      if (premiumActive) {
        activeItem = premiumActive;
        galleryType = 'premium';
      } else {
        const premiumItems = Array.from(this.elements.container?.querySelectorAll('.beezfy-gallery__media') || []);
        activeItem = premiumItems.find((item) => item.querySelector('img')) || null;
        if (activeItem) {
          galleryType = 'premium';
        }
      }
    }

    if (!activeItem) return null;

    const image = activeItem.querySelector('img');
    if (!image) return null;

    if (!image.dataset.originalSrc) {
      image.dataset.originalSrc = image.getAttribute('src') || '';
    }
    if (!image.dataset.originalSrcset) {
      const srcset = image.getAttribute('srcset');
      image.dataset.originalSrcset = srcset || '';
    }
    if (!image.dataset.originalZoomImage) {
      const zoom = image.getAttribute('data-zoom-image');
      image.dataset.originalZoomImage = zoom || '';
    }

    this.heroOverrideTarget = { container: activeItem, img: image, galleryType };
    return this.heroOverrideTarget;
  }

  overrideHeroImage(imageUrl, meta = {}) {
    const base = this.ensureHeroBaseImage();
    if (!base) return;

    const normalizedSrc = this.normalizeImageUrl(imageUrl);
    base.img.src = normalizedSrc;
    base.img.removeAttribute('srcset');
    base.img.setAttribute('data-zoom-image', normalizedSrc);
    base.container.classList.add('beezfy-media__item--swatch-override');
    base.img.classList.add('beezfy-media__image--swatch-override');

    this.heroOverrideMeta = {
      ...meta,
      src: normalizedSrc,
      filename: this.stripShopifySizeSuffix(this.extractImageFilename(normalizedSrc)),
      timestamp: Date.now()
    };
  }

  clearHeroOverride(options = {}) {
    const { force = false } = options;
    if (!this.heroOverrideMeta) return;
    if (!force && this.heroOverrideMeta.source === 'bundle-swatch') {
      return;
    }

    const base = this.ensureHeroBaseImage();
    if (!base) {
      this.heroOverrideMeta = null;
      this.heroOverrideTarget = null;
      return;
    }

    const { img, container } = base;
    const originalSrc = img.dataset.originalSrc || '';
    const originalSrcset = img.dataset.originalSrcset || '';
    const originalZoom = img.dataset.originalZoomImage || '';

    if (originalSrc) {
      img.src = originalSrc;
    }
    if (originalSrcset) {
      img.srcset = originalSrcset;
    } else {
      img.removeAttribute('srcset');
    }

    if (originalZoom) {
      img.setAttribute('data-zoom-image', originalZoom);
    } else {
      img.removeAttribute('data-zoom-image');
    }

    container.classList.remove('beezfy-media__item--swatch-override');
    img.classList.remove('beezfy-media__image--swatch-override');

    this.heroOverrideMeta = null;
    this.heroOverrideTarget = null;
  }

  initializeVariants() {
    if (!this.elements.variantInputs.length) return;

    this.elements.variantInputs.forEach(input => {
      const label = input.closest('label');
      if (label) {
        const swatch = label.querySelector('[data-swatch]');
        const button = label.querySelector('.beezfy-variant__button');
        const optionElement = swatch || button;

        // BEEZFY PREMIUM: Prevent clicks on disabled options
        if (optionElement) {
          label.addEventListener('click', (e) => {
            const optionIndexAttr = input.dataset.optionIndex;
            const optionIndex = optionIndexAttr !== undefined ? parseInt(optionIndexAttr, 10) : NaN;
            const optionValue = this.getInputOptionValue(input) || input.value || optionElement.dataset.optionValue || optionElement.dataset.swatchLabel || '';

            const isUnavailable = optionElement.classList.contains('bz-swatch--unavailable') ||
                                  optionElement.classList.contains('beezfy-swatch--disabled') ||
                                  optionElement.classList.contains('beezfy-swatch--unavailable') ||
                                  optionElement.classList.contains('beezfy-variant__button--disabled') ||
                                  optionElement.getAttribute('aria-disabled') === 'true';

            if (isUnavailable && input.disabled && !Number.isNaN(optionIndex)) {
              const resolvedVariant = this.findVariantForOptionValue(optionIndex, optionValue);

              if (resolvedVariant) {
                e.preventDefault();
                e.stopPropagation();

                if (swatch) {
                  this.lastClickedSwatch = swatch;
                  this.currentSwatchOverride = swatch;
                } else {
                  this.lastClickedSwatch = null;
                }

                this.forceVariantSelection(resolvedVariant, {
                  optionIndex,
                  optionValue,
                  swatchElement: swatch || null
                });

                return false;
              }

              e.preventDefault();
              e.stopPropagation();
              return false;
            }
          }, true);
        }

        const captureSwatch = () => {
          if (swatch) {
            this.lastClickedSwatch = swatch;
            this.currentSwatchOverride = swatch;
            this.buildSwatchSelection(swatch);
            setTimeout(() => {
              try {
                this.handleExternalMediaUpdate();
              } catch (error) {
                console.error('[BeezfyProductPremium] Swatch sync failed', error);
              }
            }, 250);
          }
        };

        if ('onpointerdown' in window) {
          label.addEventListener('pointerdown', captureSwatch);
        } else {
          label.addEventListener('mousedown', captureSwatch);
          try {
            label.addEventListener('touchstart', captureSwatch, { passive: true });
          } catch (error) {
            label.addEventListener('touchstart', captureSwatch);
          }
        }
      }

      input.addEventListener('change', (event) => {
        const swatch = this.getSwatchForInput(event?.target);
        if (swatch) {
          this.lastClickedSwatch = swatch;
          this.currentSwatchOverride = swatch;
          this.buildSwatchSelection(swatch);
        } else {
          this.lastClickedSwatch = null;
        }
        this.onVariantChange();
      });
    });

    // Initial availability check on page load
    this.updateSwatchAvailability();
  }


  getSwatchForInput(input) {
    if (!input) return null;
    const label = input.closest('label');
    if (!label) return null;
    return label.querySelector('[data-swatch]') || null;
  }

  buildSwatchSelection(swatch) {
    if (!swatch) return null;
    const container = swatch.closest('[data-swatches-container]');
    const optionName = container?.dataset?.optionName || '';
    const canonical = this.getCanonicalOptionName(optionName);
    const value = swatch.dataset.optionValue || swatch.dataset.swatchLabel || '';
    const image = this.resolveSwatchImage(swatch);
    const selection = {
      optionName: canonical,
      optionValue: value,
      image,
      element: swatch
    };
    if (canonical) {
      this.swatchSelections.set(canonical, selection);
    }
    this.latestSwatchSelection = selection;
    return selection;
  }

  findActiveColorSwatch() {
    const containers = this.elements.swatchContainers ? Array.from(this.elements.swatchContainers) : [];
    for (const container of containers) {
      if (!container || container.dataset.optionType !== 'color') continue;

      const checkedInput = container.querySelector('input[type="radio"]:checked');
      if (checkedInput) {
        const swatch = this.getSwatchForInput(checkedInput);
        if (swatch && !swatch.classList.contains('beezfy-swatch--textual')) {
          return swatch;
        }
      }

      const activeCandidate = container.querySelector('[data-swatch].beezfy-swatch--selected, [data-swatch].beezfy-swatch--active, [data-swatch].bz-swatch--active');
      if (activeCandidate && !activeCandidate.classList.contains('beezfy-swatch--textual')) {
        return activeCandidate;
      }
    }
    return null;
  }

  onVariantChange() {
    // 1. Get selected options from the form
    const selectedOptions = Array.from(this.elements.variantInputs)
      .filter(input => input.checked)
      .reduce((acc, input) => {
        const optionContainer = input.closest('[data-option-position]');
        if (optionContainer) {
          const optionIndex = parseInt(optionContainer.dataset.optionPosition, 10) - 1;
          const optionName = this.product.options[optionIndex];
          const value = this.getInputOptionValue(input);
          acc[optionName] = value;
        }
        return acc;
      }, {});

    // 2. Find the corresponding variant
    const newVariant = this.variants.find(variant => {
      return variant.options.every((optionValue, index) => {
        const optionName = this.product.options[index];
        const normalized = this.normalizeOptionKey(optionName);
        return selectedOptions[optionName] === optionValue || selectedOptions[normalized] === optionValue;
      });
    });

    if (!newVariant) {
        // SELEZIONE PARZIALE (es. solo il colore, manca la taglia): NON resettare.
        // Conserva le opzioni scelte così lo swatch resta selezionato (altrimenti syncSwatchState,
        // che deriva la selezione da currentVariant/selectedOptions, lo deseleziona ~250ms dopo).
        this.currentVariant = null;
        this.selectedOptions = selectedOptions;

        // Aggiorna la GALLERIA all'immagine del colore selezionato anche senza variante completa.
        // Serve quando la selezione arriva dal selettore colore mobile (che fa input.click() → NON
        // innesca captureSwatch/pointerdown, quindi senza questo l'immagine non cambierebbe).
        const colorInput = Array.from(this.elements.variantInputs || []).find((i) => {
          if (!i.checked) return false;
          const sw = this.getSwatchForInput(i);
          return sw && this.resolveSwatchImage(sw);
        });
        if (colorInput) {
          const sw = this.getSwatchForInput(colorInput);
          this.lastClickedSwatch = sw;
          this.currentSwatchOverride = sw;
          this.buildSwatchSelection(sw);
        } else {
          this.lastClickedSwatch = null;
        }

        this.updateVariantLabelDisplays();
        this.syncSwatchState();
        this.updateSwatchAvailability();
        this.syncMobileColorSelector();
        try { this.updateMediaForVariant(); } catch (e) {}
        return;
    }

    // Update current variant and options
    this.currentVariant = newVariant;
    this.refreshSelectedOptionsFromVariant(selectedOptions);

    // 3. Update Media Gallery
    this.updateMediaForVariant();
    setTimeout(() => {
      try {
        this.handleExternalMediaUpdate();
      } catch (error) {
        console.error('[BeezfyProductPremium] Deferred media update failed', error);
      }
    }, 250);

    // 4. Update Price, Stock, Buttons, and URL
    this.updateProductDisplay();

    // 5. Emit events for other scripts
    const detail = { variant: this.currentVariant, options: this.selectedOptions };
    this.emit('variant:changed', detail);
    document.dispatchEvent(new CustomEvent('variant:changed', { detail }));

    this.syncSwatchState();

    // Update swatch availability based on current selection
    this.updateSwatchAvailability();

    this.syncMobileColorSelector();
  }

  syncSwatchState() {
    if (!this.elements.swatchContainers || !this.elements.swatchContainers.length) {
      return;
    }

    this.elements.swatchContainers.forEach((container) => {
      const position = parseInt(container.dataset.optionPosition, 10);
      const optionIndex = Number.isNaN(position) ? -1 : position - 1;
      const optionName = this.product?.options?.[optionIndex];
      const selectedValue = optionName ? this.getOptionValue(optionName) : '';

      const normalizedSelected = this.normalizeOptionValue(selectedValue);
      const swatches = container.querySelectorAll('[data-swatch]');
      let activeSwatch = null;

      swatches.forEach((swatch) => {
        const label = swatch.closest('label');
        swatch.classList.remove('beezfy-swatch--selected', 'beezfy-swatch--active', 'bz-swatch--active');
        swatch.setAttribute('aria-pressed', 'false');
        if (label) {
          label.classList.remove('beezfy-swatch-label--active', 'beezfy-swatch-label--selected');
          const input = label.querySelector('input[type="radio"]');
          if (input) {
            input.checked = false;
            input.removeAttribute('checked');
          }
        }

        if (!activeSwatch) {
          const swatchValue = this.normalizeOptionValue(
            swatch.dataset.optionValue || swatch.dataset.swatchLabel || ''
          );
          if (swatchValue === normalizedSelected) {
            activeSwatch = swatch;
          }
        }
      });

      if (normalizedSelected && activeSwatch) {
        activeSwatch.classList.add('beezfy-swatch--selected', 'beezfy-swatch--active', 'bz-swatch--active');
        activeSwatch.setAttribute('aria-pressed', 'true');
        this.buildSwatchSelection(activeSwatch);

        const activeLabel = activeSwatch.closest('label');
        if (activeLabel) {
          activeLabel.classList.add('beezfy-swatch-label--active', 'beezfy-swatch-label--selected');
          const activeInput = activeLabel.querySelector('input[type="radio"]');
          if (activeInput) {
            activeInput.checked = true;
            activeInput.setAttribute('checked', 'checked');
          }
        }
      }

      const group = container.closest('.beezfy-variant__group');
      const selectedBadge = group?.querySelector('[data-selected-value]');
      if (selectedBadge) {
        const badgeOptionName = optionName || container.dataset.optionName || selectedBadge.dataset.optionName || '';
        if (badgeOptionName) {
          selectedBadge.dataset.optionName = badgeOptionName;
        }
        selectedBadge.textContent = selectedValue || '';
      }
    });

    if (window.beezfySwatchSystem?.updateSelectedStates) {
      window.beezfySwatchSystem.updateSelectedStates();
    }

    this.updateVariantLabelDisplays();
  }

  normalizeOptionValue(value) {
    if (!value && value !== 0) return '';
    return value
      .toString()
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ');
  }

  getInputsForOption(optionIndex) {
    if (optionIndex === undefined || optionIndex === null) return [];
    return document.querySelectorAll(`.beezfy-variant__input[data-option-index="${optionIndex}"]`);
  }

  findInputForOptionValue(optionIndex, optionValue) {
    const inputs = this.getInputsForOption(optionIndex);
    if (!inputs.length) return null;

    const targetNormalized = this.normalizeOptionValue(optionValue);

    for (const input of inputs) {
      const candidateValue = this.getInputOptionValue(input);
      if (this.normalizeOptionValue(candidateValue) === targetNormalized) {
        return input;
      }
    }

    return null;
  }

  findVariantForOptionValue(optionIndex, optionValue) {
    if (!Array.isArray(this.variants) || !this.variants.length) return null;
    if (optionIndex === undefined || optionIndex === null) return null;

    const targetNormalized = this.normalizeOptionValue(optionValue);
    if (!targetNormalized) return null;

    const optionNames = Array.isArray(this.product?.options) ? this.product.options : [];
    const currentSelections = optionNames.map((name) => {
      if (!name) return '';
      const value = this.getOptionValue(name);
      return this.normalizeOptionValue(value);
    });

    const availableCandidates = this.variants.filter((variant) => {
      if (!variant || !variant.available) return false;
      const variantValue = Array.isArray(variant.options) ? variant.options[optionIndex] : null;
      return this.normalizeOptionValue(variantValue) === targetNormalized;
    });

    if (!availableCandidates.length) {
      return null;
    }

    const exactMatch = availableCandidates.find((variant) => {
      return variant.options.every((value, idx) => {
        if (idx === optionIndex) return true;
        const normalized = this.normalizeOptionValue(value);
        const current = currentSelections[idx];
        if (!current) return true;
        return normalized === current;
      });
    });

    return exactMatch || availableCandidates[0] || null;
  }

  forceVariantSelection(variant, context = {}) {
    if (!variant) return false;

    const optionValues = Array.isArray(variant.options) ? variant.options : [];

    optionValues.forEach((value, idx) => {
      const inputs = this.getInputsForOption(idx);
      if (!inputs.length) return;

      const targetNormalized = this.normalizeOptionValue(value);

      inputs.forEach((input) => {
        const candidateValue = this.getInputOptionValue(input);
        const candidateNormalized = this.normalizeOptionValue(candidateValue);
        const matches = candidateNormalized === targetNormalized;

        if (matches) {
          if (input.disabled) {
            input.disabled = false;
            input.removeAttribute('disabled');
          }
          input.checked = true;
          input.setAttribute('checked', 'checked');
        } else if (input.checked) {
          input.checked = false;
          input.removeAttribute('checked');
        }

        const label = input.closest('label');
        if (!label) return;

        if (matches) {
          label.classList.remove('disabled', 'beezfy-swatch-label--disabled', 'beezfy-swatch-label--unavailable');
        }

        const swatch = label.querySelector('[data-swatch]');
        if (swatch) {
          if (matches) {
            swatch.classList.remove('beezfy-swatch--disabled', 'beezfy-swatch--unavailable', 'bz-swatch--unavailable');
            swatch.removeAttribute('aria-disabled');
            swatch.removeAttribute('data-unavailable-mode');
          }
        }

        const buttonEl = label.querySelector('.beezfy-variant__button');
        if (buttonEl && matches) {
          buttonEl.classList.remove('beezfy-variant__button--disabled');
          buttonEl.removeAttribute('aria-disabled');
          buttonEl.removeAttribute('data-unavailable-mode');
        }
      });
    });

    if (context.swatchElement) {
      this.lastClickedSwatch = context.swatchElement;
      this.currentSwatchOverride = context.swatchElement;
      this.buildSwatchSelection(context.swatchElement);
    }

    this.applyVariantDirectly(variant, context);
    return true;
  }

  applyVariantDirectly(variant, context = {}) {
    if (!variant) return false;

    this.currentVariant = variant;
    this.refreshSelectedOptionsFromVariant();

    this.updateMediaForVariant();
    setTimeout(() => {
      try {
        this.handleExternalMediaUpdate();
      } catch (error) {
        console.error('[BeezfyProductPremium] Deferred media update failed', error);
      }
    }, 250);

    this.updateProductDisplay();

    const detail = { variant: this.currentVariant, options: this.selectedOptions };
    this.emit('variant:changed', detail);
    document.dispatchEvent(new CustomEvent('variant:changed', { detail }));

    this.syncSwatchState();
    this.updateSwatchAvailability();

    if (context.optionIndex !== undefined && context.optionIndex !== null) {
      const focusedInput = this.findInputForOptionValue(context.optionIndex, variant.options?.[context.optionIndex]);
      if (focusedInput && typeof focusedInput.focus === 'function') {
        try {
          focusedInput.focus({ preventScroll: true });
        } catch (error) {
          focusedInput.focus();
        }
      }
    }

    return true;
  }

  /**
   * BEEZFY PREMIUM: Cross-Variant Availability Check
   * Aggiorna dinamicamente lo stato disponibile/esaurito degli swatches
   * basandosi sulle altre opzioni selezionate
   */
  updateSwatchAvailability() {
    if (!this.variants || !this.variants.length) return;

    // IMPORTANTE: Trova TUTTI i container opzioni, non solo color swatches
    const allOptionContainers = document.querySelectorAll('[data-option-position]');
    if (!allOptionContainers || !allOptionContainers.length) return;

    allOptionContainers.forEach(container => {
      const optionPosition = parseInt(container.dataset.optionPosition, 10);
      if (isNaN(optionPosition)) return;

      const optionIndex = optionPosition - 1;
      const optionName = this.product?.options?.[optionIndex];
      if (!optionName) return;

      // Trova TUTTI gli elementi selezionabili (sia swatches che bottoni)
      const swatches = container.querySelectorAll('[data-swatch]');
      const buttons = container.querySelectorAll('.beezfy-variant__button[data-option-value]');
      const allOptions = [...swatches, ...buttons];

      allOptions.forEach(optionElement => {
        // Ottieni il valore di questa opzione
        const optionValue = optionElement.dataset.optionValue ||
                           optionElement.dataset.swatchLabel ||
                           optionElement.textContent?.trim() || '';
        if (!optionValue) return;

        // LOGICA CROSS-VARIANT:
        // Controlla se ESISTE almeno una variante disponibile dove:
        // 1. Questo valore opzione corrisponde
        // 2. Le ALTRE opzioni selezionate corrispondono
        // 3. La variante è available

        const hasAvailableVariant = this.variants.some(variant => {
          if (!variant.available) return false;

          // Check se questa variante ha il valore che stiamo testando
          const variantOptionValue = variant.options?.[optionIndex];
          const optionMatches = this.normalizeOptionValue(variantOptionValue) ===
                               this.normalizeOptionValue(optionValue);

          if (!optionMatches) return false;

          // Check se le ALTRE opzioni selezionate matchano
          // (esclusa l'opzione corrente che stiamo testando)
          const otherOptionsMatch = this.product.options.every((opt, idx) => {
            if (idx === optionIndex) return true; // Skip current option

            // Leggi valore DIRETTAMENTE dal DOM (più affidabile di this.selectedOptions)
            const checkedInput = document.querySelector(`input[name="option-${idx + 1}"]:checked`);
            const selectedValue = checkedInput?.value;

            if (!selectedValue) return true; // Nessuna selezione = match

            const variantValue = variant.options?.[idx];
            return this.normalizeOptionValue(variantValue) ===
                   this.normalizeOptionValue(selectedValue);
          });

          return optionMatches && otherOptionsMatch;
        });

        // Aggiorna stato visuale
        const label = optionElement.closest('label');
        const input = label?.querySelector('input[type="radio"]');

        if (hasAvailableVariant) {
          // Disponibile: rimuovi classi/attributi disabled
          optionElement.classList.remove('bz-swatch--unavailable', 'beezfy-swatch--disabled', 'beezfy-swatch--unavailable');
          optionElement.removeAttribute('disabled');
          optionElement.removeAttribute('aria-disabled');
          optionElement.removeAttribute('data-unavailable-mode');
          if (input) input.removeAttribute('disabled');
          if (label) label.classList.remove('disabled');
        } else {
          // Non disponibile: aggiungi classi/attributi disabled
          optionElement.classList.add('bz-swatch--unavailable', 'beezfy-swatch--disabled');
          optionElement.setAttribute('disabled', 'true');
          optionElement.setAttribute('aria-disabled', 'true');
          optionElement.setAttribute('data-unavailable-mode', 'disable');
          if (input) input.setAttribute('disabled', 'true');
          if (label) label.classList.add('disabled');
        }
      });
    });
  }

  updateMediaForVariant() {
    let imageSrcToFind = null;

    let selection = null;

    if (this.lastClickedSwatch) {
      selection = this.buildSwatchSelection(this.lastClickedSwatch);
    }

    if (!selection && this.currentSwatchOverride) {
      selection = this.buildSwatchSelection(this.currentSwatchOverride);
    }

    if (!selection && this.latestSwatchSelection) {
      selection = this.latestSwatchSelection;
    }

    if (!selection) {
      const activeSwatch = this.findActiveColorSwatch();
      if (activeSwatch) {
        selection = this.buildSwatchSelection(activeSwatch);
      }
    }

    if (!selection && this.swatchSelections.size) {
      const values = Array.from(this.swatchSelections.values());
      selection = values[values.length - 1];
    }

    if (selection?.image) {
      imageSrcToFind = selection.image;
    }

    // B. If no swatch image, fall back to the variant's featured media
    if (!imageSrcToFind && this.currentVariant?.featured_image) {
      imageSrcToFind = this.currentVariant.featured_image.src;
    }

    if (!imageSrcToFind) return; // No image to update to

    const mediaMatch = this.findMediaMatch(imageSrcToFind);
    const swatchContainer = selection?.element?.closest('[data-swatches-container]')
      || this.lastClickedSwatch?.closest('[data-swatches-container]')
      || this.currentSwatchOverride?.closest?.('[data-swatches-container]')
      || null;
    const isBundleSwatch = swatchContainer?.dataset?.isBundle === 'true'
      || swatchContainer?.dataset?.isBundleOption === 'true';

    if (mediaMatch) {
      if (isBundleSwatch) {
        const optionName = selection?.optionName || swatchContainer?.dataset?.optionName || '';
        const optionValue = selection?.optionValue
          || this.lastClickedSwatch?.dataset?.optionValue
          || this.lastClickedSwatch?.dataset?.swatchLabel
          || '';
        this.overrideHeroImage(imageSrcToFind, {
          source: 'bundle-swatch',
          optionName,
          optionValue
        });
        return;
      }

      // Keep bundle overrides if the match is a generic product asset
      if (this.heroOverrideMeta?.source === 'bundle-swatch' && mediaMatch.type === 'classic') {
        const matchFilename = this.stripShopifySizeSuffix(this.extractImageFilename(imageSrcToFind));
        if (matchFilename && matchFilename !== this.heroOverrideMeta.filename) {
          return; // preserve bundle override when gallery image differs from swatch asset
        }
      }

      this.showMediaMatch(mediaMatch);
    } else if (imageSrcToFind) {
      const source = isBundleSwatch ? 'bundle-swatch' : 'variant-fallback';
      const optionName = selection?.optionName || swatchContainer?.dataset?.optionName || '';
      const optionValue = selection?.optionValue
        || this.lastClickedSwatch?.dataset?.optionValue
        || this.lastClickedSwatch?.dataset?.swatchLabel
        || '';
      this.overrideHeroImage(imageSrcToFind, {
        source,
        optionName,
        optionValue
      });
    }
  }

  updateProductDisplay() {
    if (!this.currentVariant) return;

    // Update price
    this.updatePrice();

    // Update stock
    this.updateStock();

    // Update variant ID
    if (this.elements.variantIdInput) {
      this.elements.variantIdInput.value = this.currentVariant.id;
    }

    // Update button state
    this.updateButtonState();

    // Update URL
    this.updateURL();
  }

  updatePrice() {
    const priceElement = document.querySelector('[data-price-current]');
    const compareElement = document.querySelector('[data-price-compare]');

    if (priceElement) {
      priceElement.textContent = this.formatMoney(this.currentVariant.price);
    }

    if (compareElement) {
      if (this.currentVariant.compare_at_price > this.currentVariant.price) {
        compareElement.textContent = this.formatMoney(this.currentVariant.compare_at_price);
        compareElement.style.display = '';
      } else {
        compareElement.style.display = 'none';
      }
    }
  }

  updateStock() {
    if (!this.elements.stockDisplay) return;

    const stockHTML = this.currentVariant.available
      ? '<span class="stock-available">In Stock</span>'
      : '<span class="stock-out">Out of Stock</span>';

    this.elements.stockDisplay.innerHTML = stockHTML;
  }

  updateButtonState() {
    if (!this.elements.addToCartBtn) return;

    const btnText = this.elements.addToCartBtn.querySelector('[data-add-to-cart-text]') ||
                   this.elements.addToCartBtn.querySelector('.beezfy-btn__text');

    const priceEl = this.elements.addToCartBtn.querySelector('[data-atc-price]');
    const labelEl = this.elements.addToCartBtn.querySelector('.beezfy-btn__label');
    if (this.currentVariant.available) {
      this.elements.addToCartBtn.disabled = false;
      const addText = window.beezfyTranslations?.addToCart || 'Add to Cart';
      if (priceEl) {
        // struttura a 2 righe: aggiorna solo il prezzo, lascia l'etichetta
        priceEl.textContent = this.formatMoney(this.currentVariant.price);
        if (labelEl) labelEl.textContent = addText;
      } else if (btnText) {
        btnText.textContent = `${addText} ${this.formatMoney(this.currentVariant.price)}`;
      }
    } else {
      this.elements.addToCartBtn.disabled = true;
      const soldText = window.beezfyTranslations?.soldOut || 'Sold Out';
      if (labelEl) {
        labelEl.textContent = soldText;
        if (priceEl) priceEl.textContent = '';
      } else if (btnText) {
        btnText.textContent = soldText;
      }
    }
  }

  updateURL() {
    if (!window.history || !window.history.replaceState) return;

    const url = new URL(window.location);
    url.searchParams.set('variant', this.currentVariant.id);
    window.history.replaceState({}, '', url);
  }

  // ========== QUANTITY ==========
  initializeQuantity() {
    if (!this.elements.qtyInput) return;

    this.elements.qtyBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const action = btn.dataset.qtyBtn;
        const currentQty = parseInt(this.elements.qtyInput.value) || 1;

        if (action === 'minus' && currentQty > 1) {
          this.elements.qtyInput.value = currentQty - 1;
        } else if (action === 'plus' && currentQty < 999) {
          this.elements.qtyInput.value = currentQty + 1;
        }

        this.emit('quantity:changed', { quantity: this.elements.qtyInput.value });
      });
    });

    // Direct input
    this.elements.qtyInput.addEventListener('change', () => {
      let value = parseInt(this.elements.qtyInput.value) || 1;
      value = Math.max(1, Math.min(999, value));
      this.elements.qtyInput.value = value;
    });
  }

  // ========== ADD TO CART ==========
  initializeAddToCart() {
    if (!this.elements.form) return;

    this.elements.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleAddToCart();
    });

    // Bind the method to this instance
    this.handleAddToCart = this.handleAddToCart.bind(this);

    // CRITICAL: Take control of the ATC button for variant wizard
    if (this.elements.addToCartBtn) {
      // Mark button to skip ATC unified system
      this.elements.addToCartBtn.setAttribute('data-skip-atc-system', 'true');

      // Store the handler function so we can verify it's attached
      const clickHandler = (e) => {

        // Prevent all propagation
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        // Call our handler
        this.handleAddToCart();
      };

      // Add our handler in capture phase to get priority
      this.elements.addToCartBtn.addEventListener('click', clickHandler, true);

      // Also set onclick to ensure we catch clicks
      this.elements.addToCartBtn.onclick = (e) => {
        e.preventDefault();
        this.handleAddToCart();
        return false;
      };

    }

    if (this.elements.buyNowBtn) {
      this.elements.buyNowBtn.addEventListener('click', () => {
        this.handleBuyNow();
      });
    }
  }

  async handleAddToCart(buyNow = false) {
    // IMPORTANT: Hide any blocking popups before opening wizard
    this.hideBlockingPopups();

    // Check if product has multiple variants
    const hasMultipleVariants = this.product?.variants?.length > 1;

    // Prodotto con UNA SOLA variante (es. "Taglia unica"): non c'è nulla da scegliere.
    // Con la PDP senza pre-selezione currentVariant resta null → senza questo l'ATC
    // verrebbe bloccato da "seleziona opzioni". Usiamo direttamente quella variante.
    if (!hasMultipleVariants && !this.currentVariant && this.product?.variants?.length) {
      this.currentVariant = this.product.variants[0];
    }

    // Modalità wizard (impostazione admin: settings.wizard_trigger_mode)
    //  - 'always'      → apre sempre al click su Aggiungi (prodotto variabile)
    //  - 'incomplete'  → apre SOLO se la variazione non è correttamente/consapevolmente selezionata (default)
    //  - 'manual'      → mai in automatico (si usa la selezione inline / un trigger esplicito)
    const wizardMode = window.BeezfyTheme?.settings?.wizard_trigger_mode
                    || window.beezfySettings?.wizard_trigger_mode
                    || 'incomplete';

    // Check admin settings for variant wizard
    const useVariantWizard = (window.BeezfyTheme?.settings?.enable_variant_wizard !== false) &&
                             (window.beezfySettings?.useVariantWizard !== false); // Default true

    // Determine if we need to show the wizard
    let needsVariantSelection = false;
    if (hasMultipleVariants && useVariantWizard) {
      if (wizardMode === 'always') {
        needsVariantSelection = true;
      } else if (wizardMode === 'manual') {
        needsVariantSelection = false; // mai in automatico
      } else { // 'incomplete' (default): apri solo se NON correttamente selezionata
        // La PDP ora si apre SENZA variante pre-selezionata → currentVariant resta null finché
        // l'utente non sceglie TUTTE le opzioni (onVariantChange risolve la variante completa).
        // Quindi currentVariant è il segnale affidabile (niente dipendenza dal tracking dei click).
        needsVariantSelection = !this.currentVariant || !this.currentVariant.available;
      }
    }

    if (needsVariantSelection && useVariantWizard) {
      // Open variant wizard for variant selection
      if (window.BeezfyVariantWizard && window.BeezfyVariantWizard.open) {

        // Transform product data to wizard format
        const wizardProduct = this.transformProductForWizard(this.product);


        // Pass complete product data to wizard
        const wizardData = {
          product: wizardProduct,
          currentVariant: this.currentVariant,
          selectedOptions: this.selectedOptions || {},
          onSelect: async (variant) => {
            // Callback when variant is selected in wizard
            if (variant) {
              this.currentVariant = variant;
              // Scelta nel wizard = scelta consapevole su tutte le opzioni
              (Array.isArray(this.product?.options) ? this.product.options : [])
                .forEach(o => this.userPickedOptions?.add(this.normalizeOptionKey(o)));
              this.updateProductDisplay();

              // Add to cart directly, don't call handleAddToCart again!
              const quantity = parseInt(this.elements.qtyInput?.value) || 1;

              try {
                this.setLoadingState(true);

                // Buy Now "checkout veloce": svuota il carrello prima di aggiungere
                await this.maybeClearCartForBuyNow(buyNow);

                // Use ATC system to add to cart
                if (window.BeezfyATCSystem?.addToCart) {
                  await window.BeezfyATCSystem.addToCart(variant.id, quantity);
                } else {
                  // Fallback to API
                  await this.addToCartAPI({
                    id: variant.id,
                    quantity: quantity
                  });
                }

                // Emit success event
                this.emit('cart:added', { variant, quantity });

                // Buy Now: dopo la scelta nel wizard e l'aggiunta riuscita, vai dritto al checkout (niente popup)
                if (buyNow) {
                  window.location.href = '/checkout';
                  return;
                }

                // Show success popup (respecting admin settings)
                setTimeout(() => {
                  if (window.BeezfyPopup) {
                    window.BeezfyPopup.show({
                      product: this.product,
                      variant: variant,
                      quantity: quantity
                    });
                  }
                }, 100);

              } catch (error) {
                console.error('Failed to add to cart:', error);
                this.showNotification('Failed to add to cart', 'error');
              } finally {
                this.setLoadingState(false);
              }
            }
          }
        };

        window.BeezfyVariantWizard.open(wizardData.product, wizardData.selectedOptions, wizardData.onSelect);
        return;
      } else {
        // Fallback if wizard not available
        this.showNotification(window.beezfyTranslations?.selectOptions || 'Please select all options', 'error');
        return;
      }
    }

    // Check if variant is available
    if (!this.currentVariant) {
      this.showNotification(window.beezfyTranslations?.selectOptions || 'Please select options', 'error');
      return;
    }

    if (!this.currentVariant.available) {
      this.showNotification(window.beezfyTranslations?.soldOut || 'Sold out', 'error');
      return;
    }

    const quantity = parseInt(this.elements.qtyInput?.value) || 1;

    // Show loading state
    this.setLoadingState(true);

    try {
      // Buy Now "checkout veloce": svuota il carrello prima di aggiungere
      await this.maybeClearCartForBuyNow(buyNow);

      // Use centralized ATC system
      if (window.BeezfyATCSystem) {
        // BeezfyATCSystem expects id and quantity as direct parameters
        const result = await window.BeezfyATCSystem.addToCart(
          this.currentVariant.id,
          quantity
        );

        // ATC System handles its own notifications
        // Note: ATC system might not return a result object, just check if no error thrown
      } else {
        // Fallback to direct API
        await this.addToCartAPI({
          id: this.currentVariant.id,
          quantity: quantity
        });

        // Show success notification for fallback
        this.showNotification(window.beezfyTranslations?.addedToCart || 'Added to cart!', 'success');
      }

      // Emit success event
      this.emit('cart:added', { variant: this.currentVariant, quantity });

      // Buy Now: vai al checkout SOLO dopo che l'aggiunta è andata a buon fine
      if (buyNow) {
        window.location.href = '/checkout';
        return;
      }

    } catch (error) {
      console.error('Add to cart error:', error);
      this.showNotification(window.beezfyTranslations?.cartError || 'Failed to add to cart', 'error');
    } finally {
      this.setLoadingState(false);
    }
  }

  async handleBuyNow() {
    this._buyNowClearCart = false;

    // Step "scelta carrello" (BeezWoo-style): appare SOLO se il carrello ha già prodotti
    const modal = document.querySelector('[data-cart-choice]');
    if (modal) {
      let count = 0;
      try {
        const cart = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r => r.json());
        count = cart.item_count || 0;
      } catch (e) { /* se il fetch fallisce, prosegui normale */ }

      if (count > 0) {
        const choice = await this.showCartChoice(modal);
        if (!choice) return;                       // annullato: non fare nulla
        this._buyNowClearCart = (choice === 'fast'); // 'fast' = svuota e compra solo questo
      }
    }

    // Passa l'intento "buy now": se serve il wizard, il checkout parte DOPO la scelta;
    // altrimenti parte dopo l'aggiunta riuscita. Niente più redirect prematuro (saltava il wizard).
    await this.handleAddToCart(true);
  }

  // Mostra la modale di scelta e risolve con 'fast' | 'add' | null (annullato)
  showCartChoice(modal) {
    return new Promise((resolve) => {
      const fast = modal.querySelector('[data-cart-choice-fast]');
      const add = modal.querySelector('[data-cart-choice-add]');
      const closers = Array.from(modal.querySelectorAll('[data-cart-choice-close]'));
      const finish = (val) => {
        modal.setAttribute('hidden', '');
        document.documentElement.style.overflow = '';
        if (fast) fast.removeEventListener('click', onFast);
        if (add) add.removeEventListener('click', onAdd);
        closers.forEach(c => c.removeEventListener('click', onClose));
        document.removeEventListener('keydown', onKey);
        resolve(val);
      };
      const onFast = () => finish('fast');
      const onAdd = () => finish('add');
      const onClose = () => finish(null);
      const onKey = (e) => { if (e.key === 'Escape') finish(null); };
      if (fast) fast.addEventListener('click', onFast);
      if (add) add.addEventListener('click', onAdd);
      closers.forEach(c => c.addEventListener('click', onClose));
      document.addEventListener('keydown', onKey);
      // Sposta la modale in fondo a <body> così esce da qualsiasi stacking context
      // dell'antenato e resta sopra la mobile bar.
      if (modal.parentElement !== document.body) { document.body.appendChild(modal); }
      modal.removeAttribute('hidden');
      document.documentElement.style.overflow = 'hidden';
    });
  }

  // Svuota il carrello prima di aggiungere (solo Buy Now + scelta "checkout veloce")
  async maybeClearCartForBuyNow(buyNow) {
    if (buyNow && this._buyNowClearCart) {
      try {
        await fetch('/cart/clear.js', { method: 'POST', headers: { 'Accept': 'application/json' } });
      } catch (e) { /* se fallisce, prosegue comunque */ }
    }
  }

  // Traccia se l'utente ha SCELTO consapevolmente le opzioni. La PDP auto-seleziona la prima
  // variante, quindi currentVariant non basta a sapere se la scelta è "voluta".
  setupConsciousVariantTracking() {
    if (!this.userPickedOptions) this.userPickedOptions = new Set();
    // Deep link a una variante (?variant=...) = scelta esplicita → considera tutto già scelto
    try {
      if (new URLSearchParams(location.search).has('variant') && Array.isArray(this.product?.options)) {
        this.product.options.forEach(o => this.userPickedOptions.add(this.normalizeOptionKey(o)));
      }
    } catch (e) {}
    const mark = (el) => {
      if (!el) return;
      const name = el.getAttribute('data-option-name') || el.closest('[data-option-name]')?.getAttribute('data-option-name');
      if (name) this.userPickedOptions.add(this.normalizeOptionKey(name));
    };
    document.addEventListener('click', (e) => {
      // mark() aggiunge solo se risolve un data-option-name → già sufficientemente specifico
      const sw = e.target.closest('[data-option-value], [data-swatch], .beezfy-swatch, .beezfy-variant__option, [data-variant-option]');
      if (sw) mark(sw);
    }, true);
    document.addEventListener('change', (e) => {
      const sel = e.target.closest('select[data-option-name], [data-option-name] select, [data-variant-option]');
      if (sel) mark(sel);
    }, true);
  }

  // Riflette la selezione principale sul selettore mobile (deep link / scelta da desktop).
  syncMobileColorSelector() {
    document.querySelectorAll('input[name^="mobile-option-"]').forEach((m) => {
      const pos = m.name.slice('mobile-option-'.length);
      const main = document.querySelector(`input[name="option-${pos}"]:checked`);
      const shouldCheck = !!main && main.value === m.value;
      if (m.checked !== shouldCheck) m.checked = shouldCheck;
    });
  }

  // true se l'utente ha scelto consapevolmente TUTTE le opzioni
  consciouslySelectedAll() {
    const opts = Array.isArray(this.product?.options) ? this.product.options : [];
    if (!opts.length) return true;
    return opts.every(o => this.userPickedOptions?.has(this.normalizeOptionKey(o)));
  }

  hasMissingOptions() {
    if (!this.product || !this.product.options || this.product.options.length === 0) return false;

    // Check if all options have been selected
    return this.product.options.some(option => {
      const selected = this.getOptionValue(option);
      return !selected;
    });
  }

  showVariantWizard() {
    // Use centralized variant wizard if available
    if (window.BeezfyVariantWizard) {
      const wizardProduct = this.transformProductForWizard(this.product);
      window.BeezfyVariantWizard.open(wizardProduct, this.selectedOptions);
    } else {
      this.showNotification('Please select all options', 'warning');
    }
  }

  transformProductForWizard(product) {
    if (!product || !product.options || !product.variants) {
      console.warn('Invalid product data for wizard transformation');
      return product;
    }


    // Transform Shopify product format to wizard format
    const transformedOptions = product.options.map((optionName, optionIndex) => {
      // Get all unique values for this option from variants
      const values = [...new Set(
        product.variants
          .filter(variant => variant.options && variant.options[optionIndex])
          .map(variant => variant.options[optionIndex])
      )];


      return {
        name: optionName,
        values: values,
        position: optionIndex + 1
      };
    });

    const transformedProduct = {
      ...product,
      options: transformedOptions
    };


    return transformedProduct;
  }

  async addToCartAPI(data) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      throw new Error('Failed to add to cart');
    }

    return response.json();
  }

  setLoadingState(loading) {
    if (!this.elements.addToCartBtn) return;

    const btnText = this.elements.addToCartBtn.querySelector('.beezfy-btn__text');
    const btnLoading = this.elements.addToCartBtn.querySelector('.beezfy-btn__loading');

    if (btnText) btnText.style.display = loading ? 'none' : '';
    if (btnLoading) btnLoading.style.display = loading ? '' : 'none';

    this.elements.addToCartBtn.disabled = loading;
  }

  getProductProperties() {
    // Collect any additional properties
    return {
      product_title: this.product.title,
      variant_title: this.currentVariant.title
    };
  }

  // ========== TABS ==========
  initializeTabs() {
    if (!this.elements.tabBtns.length) return;

    this.elements.tabBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const tabId = btn.dataset.tab;
        this.switchTab(tabId);
      });
    });
  }

  switchTab(tabId) {
    // Update buttons
    this.elements.tabBtns.forEach(btn => {
      btn.classList.toggle('active', btn.dataset.tab === tabId);
    });

    // Update panels
    this.elements.tabPanels.forEach(panel => {
      panel.classList.toggle('active', panel.dataset.panel === tabId);
    });
  }

  // ========== MOBILE OPTIMIZATIONS ==========
  initializeMobile() {
    if (!this.isMobile()) return;

    // Sticky add to cart on mobile
    if (this.settings.stickyAddToCart) {
      this.initializeStickyCart();
    }

    // Optimize images for mobile
    this.optimizeMobileImages();
  }

  initializeStickyCart() {
    const observer = new IntersectionObserver(
      ([entry]) => {
        document.body.classList.toggle('sticky-cart-visible', !entry.isIntersecting);
      },
      { threshold: 0 }
    );

    if (this.elements.form) {
      observer.observe(this.elements.form);
    }
  }

  optimizeMobileImages() {
    // Lazy load non-visible images
    const images = this.elements.mediaMain?.querySelectorAll('img[loading="lazy"]');
    if (!images?.length) return;

    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src || img.src;
            imageObserver.unobserve(img);
          }
        });
      });

      images.forEach(img => imageObserver.observe(img));
    }
  }

  // ========== ACCESSIBILITY ==========
  initializeAccessibility() {
    // Keyboard navigation for media
    this.elements.thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.switchMedia(index);
        }
      });
    });

    // Announce variant changes
    this.on('variant:changed', (payload) => {
      const variant = payload && payload.variant ? payload.variant : null;
      if (!variant) return;
      const announcement = variant.title || variant.name || variant.sku || '';
      if (!announcement) return;
      this.announceChange(`Selected ${announcement}`);
    });
  }

  announceChange(message) {
    const announcement = document.createElement('div');
    announcement.className = 'sr-only';
    announcement.setAttribute('aria-live', 'polite');
    announcement.textContent = message;
    document.body.appendChild(announcement);
    setTimeout(() => announcement.remove(), 1000);
  }

  // ========== UTILITIES ==========
  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.emit('product:in-view', { product: this.product });
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );

    if (this.elements.container) {
      observer.observe(this.elements.container);
    }
  }

  hideBlockingPopups() {

    // Hide all potential blocking popups
    const popups = [
      '.beezfy-atc-popup',
      '#beezfy-atc-popup',
      '.beez-atc-popup',
      '.beezfy-cart-popup',
      '.shopify-pc__banner__dialog',
      '.variant-wizard-modal',
      '.modal-backdrop',
      '.overlay'
    ];

    let hiddenCount = 0;

    popups.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        if (el) {
          // Multiple ways to hide
          el.style.display = 'none !important';
          el.style.visibility = 'hidden !important';
          el.style.opacity = '0 !important';
          el.style.zIndex = '-999999 !important';
          el.classList.remove('active', 'show', 'visible');
          el.setAttribute('hidden', 'true');
          hiddenCount++;
        }
      });
    });

    // Also remove modal-open class from body
    document.body.classList.remove('modal-open', 'overflow-hidden');

    // Force remove any high z-index elements except our wizard
    const allElements = document.querySelectorAll('*');
    allElements.forEach(el => {
      if (!el.classList.contains('bz-vw-modal') && !el.closest('.bz-vw-modal')) {
        const zIndex = window.getComputedStyle(el).zIndex;
        if (zIndex !== 'auto' && parseInt(zIndex) > 50000) {
          el.style.zIndex = '1000';
          hiddenCount++;
        }
      }
    });

  }

  showNotification(message, type = 'info') {
    if (window.BeezfyNotifications) {
      window.BeezfyNotifications.show(message, type);
    } else {
      // Fallback
      const notification = document.createElement('div');
      notification.className = `beezfy-notification beezfy-notification--${type}`;
      notification.textContent = message;
      document.body.appendChild(notification);

      setTimeout(() => {
        notification.classList.add('show');
      }, 10);

      setTimeout(() => {
        notification.classList.remove('show');
        setTimeout(() => notification.remove(), 300);
      }, 3000);
    }
  }

  formatMoney(cents) {
    // Use Shopify's money format if available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || window.Shopify.money_format);
    }
    // Fallback EUR formato italiano (mai dollari)
    return `€${(cents / 100).toFixed(2).replace('.', ',')}`;
  }

  isMobile() {
    return window.innerWidth < 768;
  }

  // ========== EVENT SYSTEM ==========
  on(event, callback) {
    document.addEventListener(`beezfy:${event}`, callback);
  }

  emit(event, detail = {}) {
    document.dispatchEvent(new CustomEvent(`beezfy:${event}`, { detail }));
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.beezfyProductPremium = new BeezfyProductPremium();
  });
} else {
  window.beezfyProductPremium = new BeezfyProductPremium();
}
